import { useState } from "react";
import api, { apiErr } from "@/lib/api";
import { MicButton } from "@/components/MicButton";
import { Container, PageHeader, Spinner, GenerateButton } from "@/components/shared";
import { toast } from "sonner";
import { GraduationCap, Copy } from "lucide-react";

const CRITERIA = [
  { key: "clarity", label: "Clarté" },
  { key: "logic", label: "Logique" },
  { key: "respect", label: "Respect" },
  { key: "persuasion", label: "Conviction" },
];

export default function Coach() {
  const [context, setContext] = useState("");
  const [draft, setDraft] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const evaluate = async () => {
    if (!draft.trim()) return;
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post("/ai/coach", { draft, context });
      setResult(data);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <Container>
      <PageHeader eyebrow="Coach" title="Coach d'argumentation"
        subtitle="Écrivez votre réponse avant de la publier. L'IA ne la remplace pas : elle la note et vous aide à progresser." />

      <div className="card-surface p-5 mb-6 space-y-3">
        <input data-testid="coach-context-input" value={context} onChange={(e) => setContext(e.target.value)}
          placeholder="Contexte / message auquel vous répondez (optionnel)"
          className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-4 py-3 outline-none text-sm placeholder:text-white/30" />
        <textarea data-testid="coach-draft-input" rows={5} value={draft} onChange={(e) => setDraft(e.target.value)}
          placeholder="Votre réponse rédigée..."
          className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-4 py-3 outline-none resize-none text-sm placeholder:text-white/30" />
        <div className="flex items-center justify-between gap-3">
          <MicButton testid="coach-draft-mic" value={draft} onChange={setDraft} />
          <GenerateButton data-testid="coach-evaluate-button" onClick={evaluate} disabled={busy || !draft.trim()}>
            <GraduationCap size={16} /> Noter ma réponse
          </GenerateButton>
        </div>
      </div>

      {busy && <Spinner label="Évaluation en cours..." />}

      {result && (
        <div className="space-y-4">
          <div className="card-surface p-6 fade-up">
            <p className="text-gold text-xs uppercase tracking-[0.15em] mb-4">Évaluation</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {CRITERIA.map((c) => (
                <div key={c.key}>
                  <div className="flex justify-between text-sm mb-1"><span className="text-white/60">{c.label}</span><span className="text-gold">{result.scores?.[c.key]}</span></div>
                  <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
                    <div className="h-full gold-gradient" style={{ width: `${result.scores?.[c.key] || 0}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="card-surface p-6 fade-up">
            <p className="text-gold text-xs uppercase tracking-[0.15em] mb-3">Retour du coach</p>
            {(result.feedback || []).map((f, i) => (
              <p key={i} data-testid={`coach-feedback-${i}`} className="text-white/85 text-sm mb-2 flex gap-2"><span className="text-gold/60">—</span> {f}</p>
            ))}
          </div>

          {result.improved_version && (
            <div className="card-surface p-6 fade-up border-gold/30">
              <div className="flex items-center justify-between mb-3">
                <p className="text-gold text-xs uppercase tracking-[0.15em]">Version plus convaincante</p>
                <button onClick={() => { navigator.clipboard.writeText(result.improved_version); toast.success("Copié !"); }}
                  className="text-white/40 hover:text-gold"><Copy size={15} /></button>
              </div>
              <p className="text-white/90 text-sm leading-relaxed">{result.improved_version}</p>
            </div>
          )}
        </div>
      )}
    </Container>
  );
}
